import React, { useEffect, useState, FormEvent, useRef } from "react";
import classNames from "classnames";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  County,
  CoverageSelectorErrorState,
  CoverageSelectorFormValues,
  CoverageSelectorInitialValues,
  CoverageSelectorSelectedValues,
  CoverageSelectorCoverageType,
  CoverageSelectorErrorType,
} from "../../@types";
import { getCounties } from "../../api";
import { useTranslate } from "../../helpers/intlHooks";
import { isValidZipcode } from "../../helpers/zipcodeHelpers";
import { getYearPartInfo } from "../../helpers/yearFlagHelpers";
import {
  AnalyticsButton,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";
import { BadgeWithIcon, ContinueWithoutLoggingInIcon } from "../../components";
import {
  PlanTypeStep,
  PlanTypeStepActions,
  ZipcodeAndCoverageYearStep,
  ZipcodeAndCoverageYearStepActions,
} from "./components";
import { PlanTypePageProps } from "./components/PlanTypeStep";

type CoverageSelectorV2Props = {
  initialFormValues?: CoverageSelectorInitialValues;
  onApply: (
    values: CoverageSelectorSelectedValues,
    analyticsButton?: AnalyticsButton
  ) => void;
  isInModal?: boolean;
  handleCancel?: () => void;
  /** set to `true` if you do not want there to be multiple steps. */
  isSinglePage?: boolean;
  /** set to `true` to show an alert that warns the user that they are
   * continuing without logging in. */
  showContinueWithoutLoggingIn?: boolean;
} & Pick<PlanTypePageProps, "showDescription" | "newToMedicare">;

enum ActiveStep {
  ZIPCODE,
  PLAN_TYPE,
}

export const CoverageSelectorV2: React.FunctionComponent<
  CoverageSelectorV2Props
> = ({
  initialFormValues,
  onApply,
  isInModal = false,
  handleCancel,
  isSinglePage,
  showDescription,
  newToMedicare,
  showContinueWithoutLoggingIn,
}) => {
  // * Hooks

  const t = useTranslate();
  const flags = useFlags();
  const { isPublicPreview, isOpenEnrollment, isOpenEnrollmentNextYearOnly } =
    getYearPartInfo(flags);
  const { mctCurrentPlanYear } = flags;
  const showCoverageYearSelector =
    isPublicPreview || isOpenEnrollment || isOpenEnrollmentNextYearOnly;
  const topOfPage = useRef<HTMLDivElement>(null);

  // * States

  const [fetching, setFetching] = useState(false);
  const [activeStep, setActiveStep] = useState<ActiveStep>(ActiveStep.ZIPCODE);
  const [errorState, setErrorState] = useState<{
    zipcode: CoverageSelectorErrorState;
    planType: CoverageSelectorErrorState;
    counties: CoverageSelectorErrorState;
  }>({
    zipcode: { error: null, message: undefined },
    planType: { error: null, message: undefined },
    counties: { error: null, message: undefined },
  });
  const [countyResults, setCountyResults] = useState<County[]>([]);
  const [currentFormValues, setCurrentFormValues] =
    useState<CoverageSelectorFormValues>({
      coverageYear: showCoverageYearSelector
        ? mctCurrentPlanYear + 1
        : mctCurrentPlanYear,
      zipcode: initialFormValues?.zipcode,
      coverageType: initialFormValues?.coverageType,
    });
  const [initialFips, setInitialFips] = useState(initialFormValues?.fips);

  // * Constants

  const findPlansButtonText = newToMedicare
    ? t("next")
    : t("coverage_selector.find_plans");

  const analyticsButton = {
    text: findPlansButtonText,
    buttonStyle: AnalyticsButtonStyle.PRIMARY,
    buttonType: AnalyticsButtonType.SUBMIT,
  };

  const isPlanTypeFormInvalid = (): boolean => {
    let hasError = false;

    if (!currentFormValues.coverageType) {
      setErrorState(errorState => ({
        ...errorState,
        planType: {
          error: CoverageSelectorErrorType.MISSING,
          message: t("coverage_selector.error.plan_type"),
        },
      }));
      hasError = true;
    } else {
      setErrorState(errorState => ({
        ...errorState,
        planType: {
          error: null,
          message: undefined,
        },
      }));
    }
    return hasError;
  };

  const isZipcodeFormInvalid = (): boolean => {
    let hasError = false;

    if (errorState.zipcode.error === CoverageSelectorErrorType.API) {
      // want to keep the api error
      hasError = true;
    } else {
      if (!isValidZipcode(currentFormValues.zipcode)) {
        setErrorState(errorState => ({
          ...errorState,
          zipcode: {
            error:
              currentFormValues.zipcode?.length === 0
                ? CoverageSelectorErrorType.MISSING
                : CoverageSelectorErrorType.INVALID,
            message: t("coverage_selector.error.zip_code"),
          },
        }));
        hasError = true;
      } else {
        // valid zipcode
        if (!currentFormValues.county) {
          setErrorState(errorState => ({
            ...errorState,
            counties: {
              error: CoverageSelectorErrorType.MISSING,
              message: "coverage_selector.error.missing_county",
            },
          }));
          hasError = true;
        } else {
          setErrorState(errorState => ({
            ...errorState,
            counties: {
              error: null,
              message: undefined,
            },
          }));
        }
      }
    }
    return hasError;
  };

  const fetchCounties = async (
    zipcode: string,
    initialFips?: string
  ): Promise<void> => {
    setFetching(true);
    try {
      const fetchedCounties = await getCounties(zipcode);
      setCountyResults(fetchedCounties);

      if (initialFips) {
        const foundCounty = fetchedCounties.find(
          county => county.fips === initialFips
        );
        if (foundCounty) {
          setCurrentFormValues(currentFormValues => ({
            ...currentFormValues,
            county: foundCounty,
          }));
        } else {
          if (fetchedCounties.length === 1) {
            setCurrentFormValues(currentFormValues => ({
              ...currentFormValues,
              county: fetchedCounties[0],
            }));
          }
        }
      } else {
        if (fetchedCounties.length === 1) {
          setCurrentFormValues(currentFormValues => ({
            ...currentFormValues,
            county: fetchedCounties[0],
          }));
        }
      }
    } catch (err) {
      setErrorState(errorState => ({
        ...errorState,
        zipcode: {
          error: CoverageSelectorErrorType.API,
          message: t("coverage_selector.error.zip_code_not_found"),
        },
      }));
    }
    setFetching(false);
  };

  const onZipcodeChange = (zipcodeInput: string) => {
    setErrorState(errorState => ({
      ...errorState,
      counties: {
        error: null,
        message: undefined,
      },
      zipcode: {
        error: null,
        message: undefined,
      },
    }));
    setCountyResults([]);
    setInitialFips(undefined);
    setCurrentFormValues(currentFormValues => ({
      ...currentFormValues,
      zipcode: zipcodeInput,
      county: undefined,
    }));

    if (isValidZipcode(zipcodeInput)) {
      fetchCounties(zipcodeInput);
    }
  };

  const onClickContinue = (e?: FormEvent<HTMLFormElement>): void => {
    e?.preventDefault();
    if (!isZipcodeFormInvalid()) {
      setActiveStep(ActiveStep.PLAN_TYPE);
      setErrorState({
        zipcode: { error: null, message: undefined },
        planType: { error: null, message: undefined },
        counties: { error: null, message: undefined },
      });
    }
  };

  const onClickStart = (e: FormEvent<HTMLFormElement>): void => {
    /** adding 16px to the top to keep the page looking a bit nice after the scroll. */
    const buffer = 16;
    e.preventDefault();
    if (isSinglePage) {
      // We want to make sure both get called, so that we get validation for both,
      // which is why they have been split up into separate constants.
      const zipCodeValid = !isZipcodeFormInvalid();
      const planTypeValid = !isPlanTypeFormInvalid();
      if (zipCodeValid && planTypeValid) {
        onApply(
          currentFormValues as CoverageSelectorSelectedValues,
          analyticsButton
        );
      } else {
        if (topOfPage.current) {
          window.scrollTo({
            top: topOfPage.current.offsetTop - buffer,
            left: 0,
            behavior: "smooth",
          });
        }
      }
      return;
    }
    if (activeStep === ActiveStep.PLAN_TYPE) {
      if (!isPlanTypeFormInvalid()) {
        onApply(
          currentFormValues as CoverageSelectorSelectedValues,
          analyticsButton
        );
      }
    } else {
      onClickContinue();
    }
  };

  const goBackToZipCode = isSinglePage
    ? undefined
    : () => {
        setActiveStep(ActiveStep.ZIPCODE);
        setErrorState({
          zipcode: { error: null, message: undefined },
          planType: { error: null, message: undefined },
          counties: { error: null, message: undefined },
        });
        setCurrentFormValues(currentForm => ({
          ...currentForm,
          coverageType: undefined,
        }));
      };

  // * useEffects

  useEffect(() => {
    if (initialFormValues?.zipcode) {
      fetchCounties(initialFormValues.zipcode, initialFormValues.fips);
    }
    /**
     * ? Consider refactoring this logic to be based entirely off of the zipcode state.
     * ? We can set the state to be initialized by initialFormValues.
     * ? That way, whenever we change the zipcode, it will auto update.
     * ? And we can have a simple dependency array of [zipcode, fetchCounties].
     * ? FWIW, we also do not need to pass initialFormValues.fips as an argument.
     * ? That could just be part of fetchCounties.
     * ? https://jira.cms.gov/browse/MCT-8783
     *  */
  }, []);

  // * Functions

  function onPlanTypeChange(planType: CoverageSelectorCoverageType) {
    setCurrentFormValues({
      ...currentFormValues,
      coverageType: planType,
    });
    setErrorState(errorState => ({
      ...errorState,
      planType: {
        error: null,
        message: undefined,
      },
    }));
  }

  return (
    <>
      {activeStep === ActiveStep.ZIPCODE && showContinueWithoutLoggingIn && (
        <BadgeWithIcon
          svgIcon={ContinueWithoutLoggingInIcon}
          theme="primary-alt-lightest"
          className="ds-u-margin-bottom--2"
        >
          {t("landing_page.badge.continue_without_logging_in")}
        </BadgeWithIcon>
      )}
      <div
        className={classNames("mct-c-coverage-selector-v2", {
          "mct-c-coverage-selector-v2--in-modal": isInModal,
        })}
        data-testid="mct-c-coverage-selector-v2"
        ref={topOfPage}
      >
        <form onSubmit={onClickStart}>
          {(errorState.planType.error ||
            errorState.zipcode.error ||
            errorState.counties.error) && (
            <div data-testid="alert" className="ds-u-margin-bottom--2">
              <Alert
                heading={t("coverage_selector.alert.heading")}
                variation="error"
                className="mct-c-coverage-selector-v2__alert"
              >
                <span>{t("coverage_selector.alert.message")}</span>
              </Alert>
            </div>
          )}
          <div className="mct-c-coverage-selector-v2__step">
            {(activeStep === ActiveStep.ZIPCODE || isSinglePage) && (
              <ZipcodeAndCoverageYearStep
                isSinglePage={isSinglePage}
                {...{
                  countiesErrorState: errorState.counties,
                  countyResults,
                  currentFips: currentFormValues.county?.fips,
                  fetching,
                  initialFips,
                  onCountyChange: county => {
                    setCurrentFormValues(formValues => ({
                      ...formValues,
                      county: JSON.parse(county),
                    }));
                  },
                  onCoverageYearChange: coverageYear => {
                    setCurrentFormValues(formValues => ({
                      ...formValues,
                      coverageYear: +coverageYear,
                    }));
                  },
                  onZipcodeChange,
                  selectedCoverageYear: currentFormValues.coverageYear,
                  selectedZipcode: currentFormValues.zipcode,
                  zipcode: currentFormValues.zipcode,
                  zipcodeErrorState: errorState.zipcode,
                }}
              />
            )}
            {(activeStep === ActiveStep.PLAN_TYPE || isSinglePage) && (
              <PlanTypeStep
                showDescription={showDescription}
                planTypeErrorState={errorState.planType}
                onPlanTypeChange={onPlanTypeChange}
                newToMedicare={newToMedicare}
                isSinglePage={isSinglePage}
              />
            )}
          </div>
          {isSinglePage && <div className="mct-c-divider" />}
          <div className={isSinglePage ? "" : "ds-u-padding-top--2"}>
            {activeStep === ActiveStep.ZIPCODE && !isSinglePage ? (
              <ZipcodeAndCoverageYearStepActions
                {...{
                  onClickContinue,
                  isInModal,
                  handleCancel,
                }}
              />
            ) : (
              <PlanTypeStepActions
                findPlansButtonText={findPlansButtonText}
                handleGoBack={goBackToZipCode}
              />
            )}
          </div>
        </form>
      </div>
    </>
  );
};
